import styles from "./ComparePlans.module.css";
import Link from "next/link";
import { GetAllV2TeamServiceTiersQuery } from "lib/types/graphql";
import { formattedPlanName } from "lib/plans/plans";
import { ButtonSubscribeFromPricing } from "./plans/ButtonSubscribeFromPricing";
import React, { Fragment } from "react";
import { formatNumber } from "lib/intl/number";
import cn from "classnames";
import { Badge } from "components/Badge/Badge";
import { IconCheckmark } from "components/Icons/IconCheckmark";
import { IconCross } from "components/Icons/IconCross";

interface Props {
  id?: string;
  teamServiceTiers: GetAllV2TeamServiceTiersQuery["team_service_tiers"];
}

export const ComparePlans = (props: Props) => {
  const { teamServiceTiers } = props;

  return (
    <section id={props.id} className={styles.main}>
      <h1>Compare all plan features</h1>
      <MobileView teamServiceTiers={teamServiceTiers} />
      <DesktopView teamServiceTiers={teamServiceTiers} />
    </section>
  );
};

const MobileView = ({ teamServiceTiers }: Pick<Props, "teamServiceTiers">) => (
  <div className={cn(styles.table, styles.mobileOnly)}>
    {teamServiceTiers.map((serviceTier) => {
      return (
        <div key={serviceTier.id}>
          <PlanHeader serviceTier={serviceTier} />
          <div className={styles.subTable}>
            <FeatureList serviceTier={serviceTier} />
          </div>
        </div>
      );
    })}
  </div>
);

const DesktopView = ({ teamServiceTiers }: Pick<Props, "teamServiceTiers">) => {
  const style = {
    "--plans-size": teamServiceTiers.length,
  } as React.CSSProperties;

  return (
    <div className={cn(styles.table, styles.desktopOnly)} style={style}>
      <div className={styles.column}>
        <div className={styles.header} />
        <FeatureList />
        <div className={styles.bottomCell} />
      </div>
      {teamServiceTiers.map((serviceTier) => {
        return (
          <div
            key={serviceTier.id}
            className={cn(
              styles.column,
              serviceTier.name === "plus" && styles.highlighted
            )}
          >
            <PlanHeader serviceTier={serviceTier} />
            {tableData.sections.map((section, index) => (
              <Fragment key={`${section.title}-${index}`}>
                <div className={styles.rowHeader} />
                {section.categories.map((row) => (
                  <div key={row.title.toString()} className={styles.cell}>
                    {renderRow(row, serviceTier)}
                  </div>
                ))}
              </Fragment>
            ))}
            <div className={styles.bottomCell}>
              <ButtonSubscribeFromPricing plan={serviceTier} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const PlanHeader = ({
  serviceTier,
}: {
  serviceTier: Props["teamServiceTiers"][number];
}) => {
  return (
    <div className={styles.header}>
      <h2>{formattedPlanName(serviceTier.name)}</h2>
      <div className={styles.price}>
        ${formatNumber(serviceTier.base_monthly_price_dollars_cents / 100)}/
        <sub>mo</sub>
      </div>
      <ButtonSubscribeFromPricing plan={serviceTier} />
    </div>
  );
};

const FeatureList = ({
  serviceTier,
}: {
  serviceTier?: Props["teamServiceTiers"][number];
}) => {
  return (
    <>
      {tableData.sections.map(({ title, categories }, index) => (
        <Fragment key={`${title}-${index}`}>
          <div className={styles.rowHeader}>{title}</div>
          {Boolean(serviceTier) && <div className={styles.rowHeader} />}

          {categories.map((row) => {
            return (
              <Fragment key={row.title.toString()}>
                <div className={cn(styles.cell, styles.secondary)}>
                  {row.title}
                </div>
                {serviceTier !== undefined && (
                  <div key={row.title.toString()} className={styles.cell}>
                    {renderRow(row, serviceTier)}
                  </div>
                )}
              </Fragment>
            );
          })}
        </Fragment>
      ))}
    </>
  );
};

interface TableData {
  sections: Array<{
    title?: string;
    categories: Array<
      {
        title: string | React.ReactElement;
      } & (
        | {
            renderRowValue: (
              serviceTier: Props["teamServiceTiers"][number]
            ) => React.ReactNode;
          }
        | { rowValue: React.ReactNode }
      )
    >;
  }>;
}

const tableData: TableData = {
  sections: [
    {
      categories: [
        {
          title: "Number of credits per month",
          renderRowValue: (serviceTier) =>
            formatNumber(serviceTier.included_nanocredits / 10 ** 9),
        },
        {
          title: "Price per extra 10 credits",
          renderRowValue: (serviceTier) =>
            `$${(serviceTier.nanocredits_cost_cents * 10 ** 8).toFixed(2)}`,
        },
        { title: "Seats", rowValue: "Unlimited" },
      ],
    },
    {
      title: "Query execution",
      categories: [
        {
          title: (
            <Link href="https://dune.com/docs/query/query-executions/?h=medium">
              <a target="_blank">Executions</a>
            </Link>
          ),
          rowValue: "Uses plan credits",
        },
        {
          title: (
            <Link href="https://dune.com/docs/app/query-editor/query-scheduler/?h=sched">
              <a target="_blank">Scheduled queries</a>
            </Link>
          ),
          rowValue: "Uses plan credits",
        },
      ],
    },
    {
      title: "Data tooling",
      categories: [
        {
          title: (
            <Link href="https://dune.com/docs/api/api-reference/upload-data/?h=upload">
              <a target="_blank">Data uploads</a>
            </Link>
          ),
          renderRowValue: (serviceTier) =>
            serviceTier.name === "community" ? (
              <Badge size="M" variant="filled" color="neutral">
                Coming soon
              </Badge>
            ) : (
              <span className={styles.success}>
                <IconCheckmark />
              </span>
            ),
        },
        {
          title: (
            <Link href="https://dune.com/docs/query/query-a-query/">
              <a target="_blank">Query view</a>
            </Link>
          ),
          rowValue: (
            <span className={styles.success}>
              <IconCheckmark />
            </span>
          ),
        },
        {
          title: (
            <Link href="https://dune.com/docs/query/query-a-query/">
              <a target="_blank">Private query views</a>
            </Link>
          ),
          renderRowValue: (serviceTier) =>
            serviceTier.name === "premium" ? (
              <span className={styles.success}>
                <IconCheckmark />
              </span>
            ) : (
              <span className={styles.danger}>
                <IconCross />
              </span>
            ),
        },
        {
          title: "Materialized views",
          renderRowValue: (serviceTier) => {
            if (serviceTier.name === "premium") {
              return (
                <span className={styles.success}>
                  <IconCheckmark />
                </span>
              );
            }

            return (
              <Badge size="M" variant="filled" color="neutral">
                Coming soon
              </Badge>
            );
          },
        },
        {
          title: "Total data storage",
          renderRowValue: (serviceTier) => renderDataStorage(serviceTier.name),
        },
      ],
    },
    {
      title: "Data export",
      categories: [
        {
          title: (
            <Link href="https://dune.com/docs/api/#api-quickstart-guides">
              <a target="_blank">API Exports</a>
            </Link>
          ),
          rowValue: "Uses plan credits",
        },
        {
          title: "API Calls per minute",
          renderRowValue: (serviceTier) =>
            formatNumber(serviceTier.api_calls_per_minute),
        },
        {
          title: (
            <Link href="https://dune.com/docs/api/api-reference/edit-queries/">
              <a target="_blank">CRUD Queries API Access</a>
            </Link>
          ),
          renderRowValue: (serviceTier) =>
            serviceTier.can_use_crud_endpoints === true ? (
              <span className={styles.success}>
                <IconCheckmark />
              </span>
            ) : (
              <span className={styles.danger}>
                <IconCross />
              </span>
            ),
        },
        {
          title: "CSV Exports per month",
          renderRowValue: (serviceTier) =>
            formatNumber(serviceTier.csv_downloads_per_month ?? 0),
        },
      ],
    },
    {
      title: "Content",
      categories: [
        {
          title: "Private dashboards",
          renderRowValue: (serviceTier) =>
            formatNumber(serviceTier.max_private_dashboards ?? 0),
        },
        {
          title: "Private queries",
          renderRowValue: (serviceTier) =>
            formatNumber(serviceTier.max_private_queries ?? 0),
        },
        {
          title: "Folders",
          renderRowValue: (serviceTier) =>
            formatNumber(serviceTier.max_folders ?? 0),
        },
        {
          title: "Hide watermark in app",
          renderRowValue: (serviceTier) =>
            serviceTier.remove_watermark === true ? (
              <span className={styles.success}>
                <IconCheckmark />
              </span>
            ) : (
              <span className={styles.danger}>
                <IconCross />
              </span>
            ),
        },
      ],
    },
  ],
};

function renderDataStorage(name: string) {
  switch (name) {
    case "community":
      return (
        <>
          10&nbsp;MB{" "}
          <Badge size="M" variant="filled" color="neutral">
            Coming soon
          </Badge>
        </>
      );
    case "plus":
      return <>15&nbsp;GB</>;
    case "premium":
      return <>50&nbsp;GB</>;
    default:
      return null;
  }
}

const renderRow = (
  row: TableData["sections"][number]["categories"][number],
  serviceTier: Props["teamServiceTiers"][number]
) => {
  return "renderRowValue" in row
    ? row.renderRowValue(serviceTier)
    : row.rowValue;
};
